/**
 * Turning an organizer's pasted stream link into an embeddable player.
 *
 * We store the URL the organizer gave us and derive the embed here, because
 * Twitch requires a `parent` parameter naming the domain doing the embedding —
 * environment-specific, so it can't live in the database.
 */

export type StreamPlatform = 'twitch' | 'youtube'

export type ParsedStream =
  | { platform: 'twitch'; kind: 'channel'; id: string }
  | { platform: 'twitch'; kind: 'video'; id: string }
  | { platform: 'youtube'; kind: 'video'; id: string }
  | { platform: 'youtube'; kind: 'channel'; id: string }

const YT_ID = /^[A-Za-z0-9_-]{11}$/

/**
 * Parse a Twitch/YouTube link. Returns null for anything we can't embed, which
 * is the signal to fall back to a plain link-out.
 */
export function parseStreamUrl(raw: string | null | undefined): ParsedStream | null {
  const trimmed = (raw ?? '').trim()
  if (!trimmed) return null

  let url: URL
  try {
    url = new URL(trimmed.startsWith('http') ? trimmed : `https://${trimmed}`)
  } catch {
    return null
  }

  const host = url.hostname.replace(/^www\./, '').toLowerCase()
  const segments = url.pathname.split('/').filter(Boolean)

  if (host === 'twitch.tv' || host === 'm.twitch.tv') {
    if (segments.length === 0) return null
    // twitch.tv/videos/123456789 is a past broadcast, not a live channel.
    if (segments[0] === 'videos' && segments[1]) {
      return { platform: 'twitch', kind: 'video', id: segments[1] }
    }
    // Ignore Twitch's own non-channel routes so /directory doesn't become a
    // "channel" named directory.
    const reserved = new Set(['directory', 'settings', 'downloads', 'jobs', 'p', 'team'])
    if (reserved.has(segments[0].toLowerCase())) return null
    return { platform: 'twitch', kind: 'channel', id: segments[0] }
  }

  if (host === 'youtu.be') {
    const id = segments[0]
    return id && YT_ID.test(id) ? { platform: 'youtube', kind: 'video', id } : null
  }

  if (host === 'youtube.com' || host === 'm.youtube.com') {
    // /watch?v=<id>
    const v = url.searchParams.get('v')
    if (v && YT_ID.test(v)) return { platform: 'youtube', kind: 'video', id: v }

    // /live/<id> and /embed/<id>
    if ((segments[0] === 'live' || segments[0] === 'embed') && segments[1]) {
      return YT_ID.test(segments[1])
        ? { platform: 'youtube', kind: 'video', id: segments[1] }
        : null
    }

    // /@handle and /channel/<id> — embeddable as a "live now" player.
    if (segments[0]?.startsWith('@')) {
      return { platform: 'youtube', kind: 'channel', id: segments[0].slice(1) }
    }
    if (segments[0] === 'channel' && segments[1]) {
      return { platform: 'youtube', kind: 'channel', id: segments[1] }
    }
    return null
  }

  return null
}

/**
 * The iframe src for a parsed stream, or null when it can't be embedded.
 *
 * `parentHost` must be the bare domain serving the page — Twitch rejects the
 * embed if it doesn't match, which is the single most common reason a Twitch
 * player renders blank.
 */
export function streamEmbedUrl(
  parsed: ParsedStream | null,
  parentHost: string
): string | null {
  if (!parsed) return null
  const parent = encodeURIComponent(parentHost)

  if (parsed.platform === 'twitch') {
    const key = parsed.kind === 'video' ? 'video' : 'channel'
    return `https://player.twitch.tv/?${key}=${encodeURIComponent(parsed.id)}&parent=${parent}&autoplay=false`
  }

  if (parsed.kind === 'video') {
    return `https://www.youtube.com/embed/${encodeURIComponent(parsed.id)}`
  }

  // A channel handle can't be embedded directly; YouTube's live_stream endpoint
  // only accepts a channel id, so a bare @handle has no embed form.
  return null
}

/** The canonical public URL to send someone to, for the "watch on …" link. */
export function streamWatchUrl(
  parsed: ParsedStream | null,
  fallback: string
): string {
  if (!parsed) return fallback
  if (parsed.platform === 'twitch') {
    return parsed.kind === 'video'
      ? `https://www.twitch.tv/videos/${parsed.id}`
      : `https://www.twitch.tv/${parsed.id}`
  }
  if (parsed.kind === 'video') return `https://www.youtube.com/watch?v=${parsed.id}`
  return `https://www.youtube.com/channel/${parsed.id}`
}

export function streamPlatformLabel(platform: StreamPlatform): string {
  return platform === 'twitch' ? 'Twitch' : 'YouTube'
}
